import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import StoreBadges from "./StoreBadges";
import {
  APP_STORE_URL,
  EMAIL,
  NAV_LINKS,
  PHONE_NL,
  SATYAM_HOLIDAYS_LABEL,
  SATYAM_HOLIDAYS_NAME,
  SATYAM_HOLIDAYS_URL,
  WHATSAPP_URL,
} from "../site/constants";
import "../pages/LandingPage.css";

function SatyamAffiliate({ className = "" }) {
  return (
    <a
      className={`lp-satyam ${className}`.trim()}
      href={SATYAM_HOLIDAYS_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${SATYAM_HOLIDAYS_LABEL} ${SATYAM_HOLIDAYS_NAME}`}
    >
      <span className="lp-satyam-label">{SATYAM_HOLIDAYS_LABEL}</span>
      <img
        className="lp-satyam-logo"
        src="/images/satyam-logo.png"
        alt={SATYAM_HOLIDAYS_NAME}
      />
    </a>
  );
}

export default function SiteLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className={`lp ${menuOpen ? "lp--menu-open" : ""}`}>
      <a href="#main-content" className="lp-skip-link">
        Ga naar inhoud
      </a>
      <header
        className={`lp-header ${scrolled ? "is-scrolled" : ""} ${
          menuOpen ? "is-menu-open" : ""
        }`}
      >
        <div className="lp-wrap lp-header-inner">
          <div className="lp-brand-lockup">
            <Link to="/" className="lp-brand" onClick={closeMenu}>
              <span className="lp-brand-mark">
                <img src="/images/app-icon.png" alt="" />
              </span>
              <span className="lp-brand-text">
                <span className="lp-brand-name">Suriwebwinkel</span>
                <span className="lp-brand-tag">Het is zo simpel.</span>
              </span>
            </Link>
            <span className="lp-brand-divider" aria-hidden="true" />
            <SatyamAffiliate className="lp-satyam--header" />
          </div>

          <nav className="lp-nav lp-nav--desktop" aria-label="Hoofdmenu">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                exact={!!link.exact}
                activeClassName="is-active"
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="lp-header-actions">
            <a
              className="lp-header-cta"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download de app
            </a>
            <button
              type="button"
              className={`lp-menu-toggle ${menuOpen ? "is-open" : ""}`}
              aria-label={menuOpen ? "Menu sluiten" : "Menu openen"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`lp-mobile-menu ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Navigatiemenu"
      >
        <div className="lp-mobile-menu-bg" aria-hidden="true">
          <span className="lp-mobile-orb lp-mobile-orb--a" />
          <span className="lp-mobile-orb lp-mobile-orb--b" />
        </div>

        <div className="lp-mobile-menu-panel">
          <div className="lp-mobile-menu-top">
            <p className="lp-mobile-kicker">Menu</p>
            <p className="lp-mobile-slogan">Het is zo simpel.</p>
          </div>

          <nav className="lp-mobile-nav" aria-label="Mobiel menu">
            {NAV_LINKS.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                exact={!!link.exact}
                activeClassName="is-active"
                onClick={closeMenu}
                style={{ "--i": i }}
              >
                <span className="lp-mobile-nav-index">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="lp-mobile-nav-label">{link.label}</span>
                <span className="lp-mobile-nav-arrow" aria-hidden="true">
                  →
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="lp-mobile-menu-foot">
            <p className="lp-mobile-foot-label">Download de app</p>
            <StoreBadges />
            <SatyamAffiliate className="lp-satyam--mobile" />
            <div className="lp-mobile-contact">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
              <span aria-hidden="true">·</span>
              <a href={`mailto:${EMAIL}`}>E-mail</a>
              <span aria-hidden="true">·</span>
              <a href={`tel:${PHONE_NL.replace(/\s/g, "")}`}>{PHONE_NL}</a>
            </div>
          </div>
        </div>
      </div>

      <main id="main-content">{children}</main>

      <footer className="lp-footer" id="contact-footer">
        <div className="lp-wrap lp-footer-grid">
          <div>
            <div className="lp-footer-brand">
              <img src="/images/app-icon.png" alt="" />
              Suriwebwinkel
            </div>
            <p className="lp-footer-about">
              De eenvoudige manier om vanuit Europa boodschappen, cadeaus en
              benodigdheden te bestellen voor familie en vrienden in Suriname.
            </p>
            <SatyamAffiliate className="lp-satyam--footer" />
          </div>

          <div>
            <h4>Shop</h4>
            <div className="lp-footer-col">
              <Link to="/producten">Producten</Link>
              <Link to="/producten">Boodschappen</Link>
              <Link to="/pakketten">Familiepakketten</Link>
              <Link to="/producten">Cadeaus</Link>
              <Link to="/cadeaubon">Cadeaubonnen</Link>
              <Link to="/app">Download de app</Link>
            </div>
          </div>

          <div>
            <h4>Service</h4>
            <div className="lp-footer-col">
              <Link to="/hoe-het-werkt">Hoe het werkt</Link>
              <Link to="/europa">Bezorg- &amp; betaalinformatie</Link>
              <Link to="/faq">Veelgestelde vragen</Link>
              <Link to="/verlanglijst">Verlanglijst maken</Link>
              <Link to="/contact">Contact</Link>
            </div>
          </div>

          <div>
            <h4>Zakelijk &amp; juridisch</h4>
            <div className="lp-footer-col">
              <Link to="/zakelijk">Zakelijke bestellingen</Link>
              <Link to="/zakelijk">Offerte aanvragen</Link>
              <Link to="/terms">Algemene voorwaarden</Link>
              <Link to="/privacy">Privacybeleid</Link>
              <Link to="/delete-account">Account verwijderen</Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                WhatsApp: {PHONE_NL}
              </a>
              <a href={`mailto:${EMAIL}`}>E-mail: {EMAIL}</a>
              <span>Openingstijden: ma–za 09:00–18:00</span>
            </div>
          </div>
        </div>
        <div className="lp-wrap lp-footer-bottom">
          <p className="lp-footer-copy">
            © {new Date().getFullYear()} Suriwebwinkel. Een merk van{" "}
            {SATYAM_HOLIDAYS_NAME}. Alle rechten voorbehouden.
          </p>
        </div>
      </footer>
    </div>
  );
}
